import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template';

import moment from 'moment';

import type { PopupData, Service } from 'webapp-types';
import { AccountSetupFeatureType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type { TrackingOriginForMetricEdit } from 'webapp/services/model-manager/metrics';

interface FeatureInfo {
	id: string;
	title: string;
	text: string;
	icon: IconName;
	baseColor: string;
	route?: string;
	onClick?: () => void;
	bgStyle?: SafeString; // appended in recommendedFeatures
	fillColor?: string; // appended in recommendedFeatures
}

export default class AccountPopupMissingFeaturesSetupComponent extends PopupInnerModalComponent<PopupData.Account.MissingFeaturesSetup> {
	@service('account') declare accountService: Service.Account;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	origin = 'missing_features_setup_popup';

	featuresConfig: FeatureInfo[] = [
		{
			id: AccountSetupFeatureType.SOURCES,
			title: 'Visualize performance from <strong>70+ one-click integrations</strong>',
			text: 'Each integration comes loaded with thousands of default metrics and pre-configured datablocks.',
			icon: 'DataManager',
			baseColor: '255, 191, 28',
			route: 'datamanager.available.index',
		},
		{
			id: AccountSetupFeatureType.DATABOARDS,
			title: 'Build drag-and-drop <strong>dashboards</strong> in minutes',
			text: 'Visualize your performance with just a few clicks. Track data from multiple integrations in one centralized place.',
			icon: 'Databoards',
			baseColor: '255, 191, 28',
			route: 'boards.index',
		},
		{
			id: AccountSetupFeatureType.CLIENTS,
			title: 'Stay organized as your <strong>portfolio grows</strong>',
			text: 'Create accounts for each client, brand, location, franchise, business unit, or other entity to organize data, control access, and tailor each account to its needs.',
			icon: 'Clients',
			baseColor: '132, 156, 163',
			route: 'clients.accounts.index',
		},
		{
			id: AccountSetupFeatureType.REPORTS,
			title: 'Automate your <strong>reporting</strong> process',
			text: 'Build and share deck-like reports right inside Databox. Schedule sends and reminders to save hours each month.',
			icon: 'Reports',
			baseColor: '255, 150, 0',
			route: 'reports.index',
		},
		{
			id: AccountSetupFeatureType.BOARD_LOOPS,
			title: 'Easily share or monitor the <strong>set of dashboards</strong>',
			text: 'Add a group of multiple dashboards together in a loop order to tell a more comprehensive story with your data.',
			icon: 'Loop',
			baseColor: '255, 191, 28',
			route: 'boards.loops.index',
		},
		{
			id: AccountSetupFeatureType.CUSTOM_METRICS,
			title: 'Create <strong>Built metrics</strong> without coding',
			text: 'Pull more granular performance insights by adding dimensions, segments, and advanced filters to selected metrics.',
			icon: 'Settings',
			baseColor: '255, 73, 41',
			onClick: () => {
				this.mmMetricsService.openCreateNewCustomMetricPopup(
					this.origin as TrackingOriginForMetricEdit,
				);
			},
		},
		{
			id: AccountSetupFeatureType.DATA_CALCULATIONS,
			title: '<strong>Calculate metrics</strong> from 2 or more tools',
			text: 'Combine data from multiple sources to calculate new metrics, KPIs, conversion rates, and more.',
			icon: 'Databoards',
			baseColor: '133, 96, 168',
			onClick: () => {
				this.mmMetricsService.openCreateNewCustomMetricPopup(
					this.origin as TrackingOriginForMetricEdit,
				);
			},
		},
		{
			id: AccountSetupFeatureType.GOALS,
			title: 'Stay up-to-date on progress towards your <strong>goals</strong>',
			text: "Set goals for any metric you're tracking. Watch your progress and get notified as you get closer to hitting your goal.",
			icon: 'OkRs',
			baseColor: '132, 156, 163',
			route: 'goals.index',
		},
		{
			id: AccountSetupFeatureType.SNAPSHOTS,
			title: 'Use <strong>snapshots</strong> to make data a part of your routine',
			text: 'Schedule snapshots of any dashboard to be delivered directly to your inbox at regular time intervals.',
			icon: 'Clock',
			baseColor: '29, 154, 214',
			route: 'settings.notifications',
		},
		{
			id: AccountSetupFeatureType.SCORECARDS,
			title: 'Improve team collaboration with <strong>scorecards</strong>',
			text: 'Get daily, weekly, or monthly progress updates on your most-important metrics. Deliver your data to anyone, on any device.',
			icon: 'Scorecard',
			baseColor: '46, 193, 214',
			route: 'settings.notifications',
		},
		{
			id: AccountSetupFeatureType.METRIC_DIGESTS,
			title: 'Get <strong>weekly updates</strong> on your top metrics',
			text: 'Receive detailed information about the performance of your top metrics directly in your inbox, on a weekly basis.',
			icon: 'Metrics',
			baseColor: '255, 73, 41',
			route: 'settings.notifications',
		},
		{
			id: AccountSetupFeatureType.ALERTS,
			title: 'Receive performance <strong>alerts</strong> when they matter most',
			text: 'Automatically and proactively monitor your data. Alerts will notify you about sudden changes or emerging trends.',
			icon: 'Notifications',
			baseColor: '255, 150, 0',
			route: 'settings.notifications',
		},
		{
			id: AccountSetupFeatureType.WHITE_LABELING,
			title: '<strong>White label</strong> Databox to be a self-branded tool',
			text: 'Have your own fully branded login and web-app. Share and present branded dashboards from your custom domain.',
			icon: 'Paint',
			baseColor: '29, 154, 214',
			route: '',
			onClick: () => {
				this.aiChatService.onNavigateToWhiteLabeling();
			},
		},
	];

	recommendedFeatures: FeatureInfo[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.recommendedFeatures = this.featuresConfig
			.filter((item) => {
				return (this.popupData.features.map((item) => item.id) as string[]).includes(
					item.id,
				);
			})
			.map((item) => {
				return {
					...item,
					bgStyle: htmlSafe(`background-color: rgba(${item.baseColor}, 0.1);`),
					fillColor: `rgb(${item.baseColor})`,
				};
			});

		this.analyticsService.event(TrackingEvent.FEATURES_POPUP_SHOWN, {
			choices: this.recommendedFeatures.map((item) => item.id),
		});
	}

	get popupTitle(): string {
		const becamePaying = this.billingService.becamePaying || 0;

		if (moment.unix(becamePaying).add(90, 'days').unix() < moment().unix()) {
			return 'Congratulations on becoming data-aware';
		}

		return 'Here are some features you’ve missed';
	}

	@action
	next(item: FeatureInfo): void {
		this.analyticsService.event(TrackingEvent.FEATURES_POPUP_COMPLETED, { choice: item.id });
		if (item.route) {
			this.navigationService.transitionToRoute(item.route);
		} else if (item.onClick) {
			item.onClick();
		}
		this.close();
	}

	@action
	requestHelp(): void {
		window.open(this.accountService.contactOwnerLink, '_blank');
	}
	<template>
		<div class="content">
			<div class="sides-ct has-wide-left-side">
				<div class="side side-left">
					<img src="/images/svg/wizard.svg" width="auto" height="113" alt />
					<div class="ct">
						<small>DATA AWARE</small>
						<h2>{{this.popupTitle}}</h2>
					</div>
				</div>
				<div class="side side-right">

					<h2>{{this.popupTitle}}</h2>
					{{! shown only on mobile when side-left hidden }}

					<ul class="items-list items-list--selectable">
						{{#each this.recommendedFeatures as |item|}}
							<li role="button" class="item" {{on "click" (fn this.next item)}}>
								<span class="icon" style={{item.bgStyle}}>
									<CommonUiIconComponent
										@name={{item.icon}}
										@size="md"
										@fill={{item.fillColor}}
									/>
								</span>
								<div class="t">{{{item.title}}}</div>
								<p>{{item.text}}</p>
								<CommonUiIconComponent
									@name="ArrowRight"
									@size="md"
									@class="item-arrow"
								/>
							</li>
						{{/each}}
					</ul>

					<div class="controls2">
						<div class="controls2__right">
							<SubmitButtonComponent
								@text="I'll setup them on my own"
								@theme="grey-empty"
								@noDelay={{true}}
								@onTrigger={{this.close}}
							/>
							<SubmitButtonComponent
								@text="Request help"
								@size="big"
								@icon="UserCenter"
								@noDelay={{true}}
								@onTrigger={{this.requestHelp}}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	</template>
}
